// ─── PRODUCT CONFIG ────────────────────────────────────────────────────────────
//
// Split out of product-ui.js (further Phase 5 decomposition, per the
// vinylpac-refactoring-guide skill's "default to shared components, not one
// big file" principle). Covers material grid, jar color swatches, upload
// tabs, mylar panel upload, category switching, and price updates -- the
// "what product/options is the customer configuring" concerns, as opposed
// to preview-render.js's "what does the preview look like" concerns.
//
// Loaded via <script src> in the same position product-ui.js used to load,
// BEFORE the remaining inline block containing INIT (INIT calls
// buildMaterialGrid/switchCategory synchronously and unguarded -- see the
// vinylpac-refactoring-guide skill, lesson 2, for why load order here
// matters). preview-render.js loads immediately after this file; nothing
// in this file executes at load time (it's all function declarations plus
// two var initializers), so there's no ordering risk between the two --
// they only need to both be loaded before anything is actually called.
//
// Depends on globals declared in product-state.js / pricing-engine.js /
// sq-panel.js / configurator.html's own script, and calls into
// preview-render.js's showArtworkPreview()/setPreviewHint() (safe since
// both files are loaded before either is invoked).

// ─── MATERIAL GRID ───────────────────────────────────────────────────────────

// ── Material swatch texture renderer ────────────────────────────────────────
// Draws a 56×56 canvas tile representing each material type.
// Uses the same colour science as the full-size preview renderers.
function drawMaterialSwatch(canvas, matId) {
  var S = canvas.width; // 56
  var ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, S, S);

  if (matId === 'white-vinyl') {
    // Warm white with faint paper gradient and subtle grain
    var g = ctx.createRadialGradient(S*0.35, S*0.35, 0, S*0.5, S*0.5, S*0.72);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, '#e8e8e8');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
    // Grain dots
    for (var i = 0; i < 90; i++) {
      ctx.beginPath();
      ctx.arc(Math.random()*S, Math.random()*S, Math.random()*0.7+0.1, 0, Math.PI*2);
      ctx.fillStyle = 'rgba(0,0,0,' + (Math.random()*0.04+0.01) + ')';
      ctx.fill();
    }

  } else if (matId === 'clear') {
    // Checkerboard transparency indicator + pale blue edge shimmer
    var tile = 7;
    for (var cy = 0; cy < S; cy += tile) {
      for (var cx = 0; cx < S; cx += tile) {
        ctx.fillStyle = ((Math.floor(cx/tile) + Math.floor(cy/tile)) % 2 === 0)
          ? 'rgba(195,195,195,0.55)' : 'rgba(255,255,255,0.90)';
        ctx.fillRect(cx, cy, tile, tile);
      }
    }
    var eg = ctx.createRadialGradient(S*0.28, S*0.28, 0, S*0.5, S*0.5, S*0.52);
    eg.addColorStop(0, 'rgba(255,255,255,0.45)');
    eg.addColorStop(0.6, 'rgba(200,230,255,0.15)');
    eg.addColorStop(1,   'rgba(160,210,255,0.25)');
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = eg;
    ctx.fillRect(0, 0, S, S);

  } else if (matId === 'mirror' || matId === 'chrome') {
    // Chrome: diagonal silver gradient + bright specular streak
    var mg = ctx.createLinearGradient(0, 0, S, S);
    mg.addColorStop(0,   '#f5f7f9');
    mg.addColorStop(0.18,'#dce4ec');
    mg.addColorStop(0.38,'#a8b8c8');
    mg.addColorStop(0.55,'#c4d0da');
    mg.addColorStop(0.75,'#e4eaf0');
    mg.addColorStop(1,   '#a0aeb8');
    ctx.fillStyle = mg;
    ctx.fillRect(0, 0, S, S);
    // Specular highlight streak
    var hl = ctx.createLinearGradient(S*0.05, S*0.05, S*0.55, S*0.38);
    hl.addColorStop(0,   'rgba(255,255,255,0.92)');
    hl.addColorStop(0.5, 'rgba(255,255,255,0.30)');
    hl.addColorStop(1,   'rgba(255,255,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(0, 0, S, S);
    // Faint secondary reflection at bottom-right
    var hl2 = ctx.createLinearGradient(S*0.6, S*0.7, S, S);
    hl2.addColorStop(0, 'rgba(255,255,255,0)');
    hl2.addColorStop(1, 'rgba(255,255,255,0.35)');
    ctx.fillStyle = hl2;
    ctx.fillRect(0, 0, S, S);

  } else if (matId === 'holo') {
    // Vibrant rainbow diagonal + silver overlay to read as metallic foil
    var hg = ctx.createLinearGradient(0, S, S, 0);
    hg.addColorStop(0,    '#ff6ec7');
    hg.addColorStop(0.18, '#fffb00');
    hg.addColorStop(0.36, '#00ffea');
    hg.addColorStop(0.54, '#a855f7');
    hg.addColorStop(0.72, '#3b82f6');
    hg.addColorStop(0.88, '#ff9de2');
    hg.addColorStop(1,    '#ff6ec7');
    ctx.fillStyle = hg;
    ctx.fillRect(0, 0, S, S);
    // Silver overlay — lighten highlights, deepen troughs → metallic feel
    var sg = ctx.createRadialGradient(S*0.28, S*0.28, 0, S*0.5, S*0.5, S*0.68);
    sg.addColorStop(0, 'rgba(255,255,255,0.55)');
    sg.addColorStop(0.5,'rgba(220,220,220,0.10)');
    sg.addColorStop(1,  'rgba(180,180,180,0.05)');
    ctx.globalCompositeOperation = 'overlay';
    ctx.fillStyle = sg;
    ctx.fillRect(0, 0, S, S);
    ctx.globalCompositeOperation = 'source-over';
    // Tiny sparkle highlights
    for (var hi = 0; hi < 8; hi++) {
      ctx.beginPath();
      ctx.arc(Math.random()*S, Math.random()*S, Math.random()*1.2+0.4, 0, Math.PI*2);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fill();
    }

  } else if (matId === 'glitter') {
    // A photo of the shop's glitter vinyl (mirror silver under rainbow grains), the jar
    // labels' Glitter swatch too. Silver until it loads.
    ctx.fillStyle = '#a6abb1';
    ctx.fillRect(0, 0, S, S);
    var photo = new Image();
    photo.onload = function() { ctx.drawImage(photo, 0, 0, S, S); };
    photo.src = (window.VP_CDN+"jar-labels__swatch-glitter.jpg?v=40fb8dea88");

  } else if (matId === 'gold') {
    var gg = ctx.createLinearGradient(0, 0, S, S);
    gg.addColorStop(0,   '#fff4c8');
    gg.addColorStop(0.22,'#e8c45a');
    gg.addColorStop(0.48,'#c9952a');
    gg.addColorStop(0.7, '#f0d78a');
    gg.addColorStop(1,   '#a67a1a');
    ctx.fillStyle = gg;
    ctx.fillRect(0, 0, S, S);
    var ghl = ctx.createLinearGradient(S*0.05, S*0.05, S*0.55, S*0.38);
    ghl.addColorStop(0,   'rgba(255,255,240,0.9)');
    ghl.addColorStop(0.5, 'rgba(255,230,140,0.25)');
    ghl.addColorStop(1,   'rgba(255,255,255,0)');
    ctx.fillStyle = ghl;
    ctx.fillRect(0, 0, S, S);

  } else if (matId === 'reflective') {
    var rg = ctx.createLinearGradient(0, 0, S, S);
    rg.addColorStop(0,   '#e9e9e9');
    rg.addColorStop(0.4, '#9c9c9c');
    rg.addColorStop(0.7, '#c8c8c8');
    rg.addColorStop(1,   '#707070');
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, S, S);
    for (var ri = 0; ri < 90; ri++) {
      ctx.beginPath();
      ctx.arc(Math.random()*S, Math.random()*S, Math.random()*1.4+0.3, 0, Math.PI*2);
      ctx.fillStyle = Math.random() > 0.5
        ? 'rgba(247,247,247,' + (Math.random()*0.8+0.15) + ')'
        : 'rgba(180,220,160,' + (Math.random()*0.5+0.2) + ')';
      ctx.fill();
    }
  }
}

function buildMaterialGrid() {
  var grid = document.getElementById('materialGrid');
  grid.innerHTML = '';
  MATERIALS.forEach(function(m, i) {
    var sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'mat-swatch' + (i === currentMat ? ' active' : '');
    sw.setAttribute('aria-pressed', String(i === currentMat));
    sw.setAttribute('data-qa', 'material-btn-' + m.id);
    sw.onclick = function() { selectMaterial(i); };
    var c = document.createElement('canvas');
    c.width = c.height = 56;
    drawMaterialSwatch(c, m.id);
    var lbl = document.createElement('span');
    lbl.textContent = m.label;
    sw.appendChild(c);
    sw.appendChild(lbl);
    grid.appendChild(sw);
  });
}

// Materials a product may be printed on; null = every material (Josh 10/17).
// Roll labels: White Vinyl or Holo. Static Cling: static cling only (its one
// tile reads "Static Cling"; the id stays white-vinyl so pricing is unchanged).
// Clear stickers: Clear only. Laminate stays open on all three.
function vpIsStaticCling() {
  var chip = typeof vpQuoteChipId === 'function' ? vpQuoteChipId() : '';
  var name = typeof vpQuoteProductName === 'function' ? vpQuoteProductName() : '';
  return chip === 'static-cling' || name.indexOf('cling') !== -1;
}

// The material's name on the cart line, artwork file name and template; the
// checkout Worker's builderProperties() (sue-pricing.js) says the same.
function vpMaterialLabel() {
  var mat = MATERIALS[currentMat];
  return vpIsStaticCling() ? 'Static Cling' : (mat ? mat.label : '');
}

function vpAllowedMaterials() {
  if (typeof vpQuoteCut === 'function' && vpQuoteCut() === 'roll') return ['white-vinyl', 'holo'];
  if (vpIsStaticCling()) return ['white-vinyl'];
  var chip = typeof vpQuoteChipId === 'function' ? vpQuoteChipId() : '';
  var name = typeof vpQuoteProductName === 'function' ? vpQuoteProductName() : '';
  if (chip === 'clear' || chip === 'emb-clear' ||
      ((currentCat === 'custom-stickers' || currentCat === 'embossed-stickers') && name.indexOf('clear') !== -1)) return ['clear'];
  return null;
}

function selectMaterial(idx) {
  // A carried, restored or linked material this product can't use falls back to its own.
  var allowedMats = vpAllowedMaterials();
  if (allowedMats && MATERIALS[idx] && allowedMats.indexOf(MATERIALS[idx].id) === -1) {
    for (var ai = 0; ai < MATERIALS.length; ai++) {
      if (MATERIALS[ai].id === allowedMats[0]) { idx = ai; break; }
    }
  }
  currentMat = idx;
  document.querySelectorAll('#materialGrid .mat-swatch').forEach(function(el, i) {
    el.classList.toggle('active', i === idx);
    el.setAttribute('aria-pressed', String(i === idx));
  });

  // For die-cut products the selected material IS the backing material —
  // sync currentUnderlay so renderDieCutPreview uses the right fill
  if (isDieCutProduct()) {
    currentUnderlay = MATERIALS[idx].id;
  }

  // Re-apply the material's finish overlay on top of the uploaded artwork, or on
  // this product's fixed default-material photo if nothing's been uploaded yet.
  // The base image itself never changes -- only the overlay effect does.
  showArtworkPreview();
  updatePrice();
}

function setEmboss(id) {
  id = (typeof vpNormEmboss === 'function') ? vpNormEmboss(id) : (id === 'light' ? 'std' : id);
  var allowed = { none: 1, std: 1, heavy: 1 };
  if (!allowed[id]) id = 'none';
  if (typeof vpCutAllowsEmboss === 'function' && !vpCutAllowsEmboss() && id !== 'none') id = 'none';
  currentEmboss = id;
  if (id !== 'none') {
    // Remember the depth for this product, so switching embossing back on (even
    // after visiting another product) comes back as Standard or Heavy as left.
    var depthKey = currentChipId || currentCat;
    (surfaceFinishChoices[depthKey] || (surfaceFinishChoices[depthKey] = {})).emboss = id;
    currentFinish = 'none';
    currentSpecialty = 'none';
  } else if (currentFinish === 'none') {
    currentFinish = 'glossy';
  }
  updateEmbossSection();
  if (typeof _syncLaminateChips === 'function') _syncLaminateChips();
  if (typeof _refreshFinishPreview === 'function') _refreshFinishPreview();
  updatePrice();
  try {
    if (window.vpStickerPreview && typeof window.vpStickerPreview.sync === 'function') {
      window.vpStickerPreview.sync();
    }
  } catch (e) {}
}

var surfaceFinishChoices = {};

function setSurfaceFinish(mode) {
  var key = currentChipId || currentCat;
  var saved = surfaceFinishChoices[key] || (surfaceFinishChoices[key] = {});
  if (mode === 'emboss') {
    if (currentEmboss !== 'none') return;
    if (typeof setLaminateMore === 'function') setLaminateMore(false);
    setEmboss(saved.emboss || 'std');
  } else if (currentEmboss !== 'none') {
    saved.emboss = currentEmboss;
    setFinish('glossy');
  }
}

// The Embossing card's switch (Josh 10/15, Sue's idea): on locks the laminate
// tiles (greyed, not tappable) and folds them back to the opening Gloss row;
// only switching embossing off unlocks them, starting again from Gloss.
// The chosen depth (Standard / Heavy) is remembered per product.
function toggleEmbossing() {
  if (currentCat === 'embossed-stickers') return;
  setSurfaceFinish(currentEmboss === 'none' ? 'emboss' : 'laminate');
}

function updateEmbossSection() {
  if (typeof vpGetPrintKind === 'function' && vpGetPrintKind()) {
    var printChooser = document.getElementById('surfaceFinishSection');
    if (printChooser) printChooser.hidden = true;
    var printFinish = document.getElementById('finishSection');
    if (printFinish) printFinish.style.display = 'none';
    var printEmboss = document.getElementById('embossSection');
    if (printEmboss) printEmboss.style.display = 'none';
    var printSummary = document.getElementById('surfaceSelectionSummary');
    if (printSummary) printSummary.hidden = true;
    return;
  }
  var allow = typeof vpCutAllowsEmboss === 'function' && vpCutAllowsEmboss();
  if (!allow) currentEmboss = 'none';
  // Roll labels (Josh 10/17, replaces the 10/10 gloss-only rule): White Vinyl or Holo only, Gloss or Matte
  // laminate, no specialty laminates, no embossing, no white ink.
  var rollGloss = typeof vpQuoteCut === 'function' && vpQuoteCut() === 'roll';
  var rollForced = rollGloss && (currentSpecialty !== 'none' || (currentFinish !== 'glossy' && currentFinish !== 'matte'));
  if (rollGloss) {
    if (currentFinish !== 'matte') currentFinish = 'glossy';
    currentSpecialty = 'none';
  }
  // Josh 10/31: "Roll labels only go up to 9x9" (the price engine refuses bigger rolls); other stickers keep 24.
  ['customW', 'customH'].forEach(function(id) {
    var box = document.getElementById(id);
    if (box) box.max = rollGloss ? '9' : '24';
  });
  // Die-cut-only laminates (Josh 10/18: Stardust "thats only a diecut only option"; chips marked data-cut="die").
  // Any other cut hides them, and a pick from a chip, ?lam= link, saved state or cart edit falls back to Gloss.
  var dieCut = typeof vpQuoteCut === 'function' && vpQuoteCut() === 'die' &&
    !(typeof vpPricedAsCut === 'function' && vpPricedAsCut());   // Clear / Cling / Front Adhesive: die-cut price only (10/31)
  var dieOnlyForced = !dieCut && currentSpecialty !== 'none' &&
    !!document.querySelector('#finishToggle .lam-chip[data-cut="die"][data-sf="' + currentSpecialty + '"]');
  if (dieOnlyForced) currentSpecialty = 'none';
  var allowedMats = vpAllowedMaterials();
  var clingTile = vpIsStaticCling();
  document.querySelectorAll('#materialGrid .mat-swatch').forEach(function(el, i) {
    var off = !!allowedMats && !!MATERIALS[i] && allowedMats.indexOf(MATERIALS[i].id) === -1;
    el.hidden = off;
    el.style.display = off ? 'none' : '';
    var lbl = el.querySelector('span');
    if (lbl && MATERIALS[i]) lbl.textContent = clingTile && MATERIALS[i].id === 'white-vinyl' ? 'Static Cling' : MATERIALS[i].label;
  });
  if (allowedMats && MATERIALS[currentMat] && allowedMats.indexOf(MATERIALS[currentMat].id) === -1) {
    selectMaterial(currentMat); // the guard in selectMaterial picks this product's material
  }
  var embossed = currentEmboss !== 'none';
  if (embossed) {
    currentFinish = 'none';
    currentSpecialty = 'none';
  } else if (currentFinish === 'none') {
    currentFinish = 'glossy';
  }
  var hidden = !!CATEGORIES[currentCat].hideFinish;
  var chooser = document.getElementById('surfaceFinishSection');
  if (chooser) chooser.hidden = hidden;
  var embossSwitch = document.getElementById('embossSwitch');
  if (embossSwitch) {
    embossSwitch.setAttribute('aria-checked', String(embossed));
    embossSwitch.disabled = currentCat === 'embossed-stickers';
  }
  var embossDepth = document.getElementById('embossToggle');
  if (embossDepth) embossDepth.hidden = !embossed;
  var embossChoice = document.getElementById('surfaceEmboss');
  if (embossChoice) {
    embossChoice.disabled = !allow;
    embossChoice.setAttribute('aria-pressed', String(embossed));
  }
  var laminateChoice = document.getElementById('surfaceLaminate');
  if (laminateChoice) laminateChoice.setAttribute('aria-pressed', String(!embossed));
  var help = document.getElementById('surfaceFinishHelp');
  if (help) help.textContent = !allow
    ? 'Laminate options for this product. Embossing is available on die-cut, kiss-cut and sheet stickers.'
    : embossed ? 'Raised detail you can feel. Embossed stickers are made without laminate.'
    : 'A smooth protective film over your print. Choose laminate or embossing, not both.';
  var summary = document.getElementById('surfaceSelectionSummary');
  if (summary) {
    summary.hidden = hidden;
    var laminateName = currentSpecialty !== 'none' ? SPECIALTY_LABELS[currentSpecialty] || currentSpecialty : currentFinish === 'matte' ? 'Matte' : 'Gloss';
    summary.textContent = rollGloss ? (currentFinish === 'matte' ? 'Matte' : 'Gloss') + ' roll labels'
      : embossed ? EMBOSS_LABELS[currentEmboss] + ' emboss · No laminate' : laminateName + ' laminate';
  }
  var finish = document.getElementById('finishSection');
  // Embossing greys the laminate out instead of removing it; the embossed
  // product line has no laminate to go back to, so it still hides there.
  var lamLocked = embossed && currentCat === 'embossed-stickers';
  if (finish) {
    finish.style.display = hidden || lamLocked ? 'none' : '';
    finish.classList.toggle('roll-lam', rollGloss);
    finish.classList.toggle('lam-not-die', !dieCut);
    if (rollGloss && finish.classList.contains('lam-open') && typeof setLaminateMore === 'function') setLaminateMore(false);
    finish.classList.toggle('lam-off', embossed);
    // Locked, not just grey: a tap on a laminate can never undo embossing.
    finish.querySelectorAll('.lam-chip').forEach(function(chip) {
      chip.disabled = embossed;
      if (embossed) chip.setAttribute('aria-disabled', 'true'); else chip.removeAttribute('aria-disabled');
    });
    if (embossed && finish.classList.contains('lam-open') && typeof setLaminateMore === 'function') setLaminateMore(false);
  }
  var lamNote = document.getElementById('lamNote');
  if (lamNote) lamNote.textContent = embossed
    ? 'Off while embossing is on'
    : 'A protective film over your print';
  var sec = document.getElementById('embossSection');
  if (sec) sec.style.display = !hidden && allow ? '' : 'none';
  var row = document.getElementById('embossToggle');
  if (row) row.querySelectorAll('[data-emboss]').forEach(function(btn) {
    var on = btn.getAttribute('data-emboss') === currentEmboss;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-pressed', String(on));
  });
  if (typeof _syncLaminateChips === 'function') _syncLaminateChips();
  if (rollForced || dieOnlyForced) {
    if (typeof _refreshFinishPreview === 'function') _refreshFinishPreview();
    if (typeof _syncLaminate3D === 'function') _syncLaminate3D();
  }
}

function applyQuoteUrlParams(params) {
  params = params || (typeof URLSearchParams === 'function' ? new URLSearchParams(window.location.search) : null);
  if (!params) return;
  var fin = String(params.get('finish') || '').toLowerCase();
  var emb = String(params.get('emboss') || '').toLowerCase();
  var FIN_MAT = {
    clear: 'clear', glitter: 'glitter', holo: 'holo', holographic: 'holo',
    reflective: 'reflective', chrome: 'chrome', mirror: 'chrome', gold: 'gold'
  };
  function selectMatId(id) {
    if (typeof MATERIALS === 'undefined') return;
    for (var i = 0; i < MATERIALS.length; i++) {
      if (MATERIALS[i].id === id) { selectMaterial(i); return; }
    }
  }
  if (fin === 'matte') {
    selectMatId('white-vinyl');
    if (typeof setFinish === 'function') setFinish('matte');
  } else if (fin === 'gloss' || fin === 'glossy') {
    selectMatId('white-vinyl');
    if (typeof setFinish === 'function') setFinish('glossy');
  } else if (FIN_MAT[fin]) {
    selectMatId(FIN_MAT[fin]);
    if (typeof setFinish === 'function') setFinish('glossy');
  }
  // ?lam= laminate override (test/deep-link). Accepts the internal ids
  // (glitter-lam-* / holo-lam-*), the 3D laminate values (glitter-* / holo-*),
  // the finish names (apollo-silver, orion-gold, cosmic-shift,
  // mars-red, neptune, fracture, prismatic, super-cosmic, hex, hearts) and the legacy
  // silver-glitter / gold-glitter / rainbow-glitter aliases.
  var lam = String(params.get('lam') || params.get('laminate') || '').toLowerCase();
  var LAM = {
    gloss: 'glossy', glossy: 'glossy', matte: 'matte', satin: 'satin',
    // Galactic Series (metal flake)
    'glitter-silver': 'glitter-lam-silver', 'silver-glitter': 'glitter-lam-silver', 'glitter-lam-silver': 'glitter-lam-silver', 'apollo-silver': 'glitter-lam-silver', 'apollo': 'glitter-lam-silver',
    'glitter-gold': 'glitter-lam-gold', 'gold-glitter': 'glitter-lam-gold', 'glitter-lam-gold': 'glitter-lam-gold', 'orion-gold': 'glitter-lam-gold', 'orion': 'glitter-lam-gold',
    'glitter-rainbow': 'glitter-lam-rainbow', 'rainbow-glitter': 'glitter-lam-rainbow', 'glitter-lam-rainbow': 'glitter-lam-rainbow', 'cosmic-shift': 'glitter-lam-rainbow',
    'glitter-red': 'glitter-lam-red', 'red-glitter': 'glitter-lam-red', 'glitter-lam-red': 'glitter-lam-red', 'mars-red': 'glitter-lam-red', 'mars': 'glitter-lam-red',
    'glitter-neptune': 'glitter-lam-neptune', 'glitter-lam-neptune': 'glitter-lam-neptune', 'neptune': 'glitter-lam-neptune',
    'glitter-stardust': 'glitter-lam-stardust', 'glitter-lam-stardust': 'glitter-lam-stardust', 'stardust': 'glitter-lam-stardust',
    // Spectrum (holographic)
    'holo-fracture': 'holo-lam-fracture', 'holo-lam-fracture': 'holo-lam-fracture', 'fracture': 'holo-lam-fracture',
    'holo-prismatic': 'holo-lam-prismatic', 'holo-lam-prismatic': 'holo-lam-prismatic', 'prismatic': 'holo-lam-prismatic',
    'holo-cosmic': 'holo-lam-cosmic', 'holo-lam-cosmic': 'holo-lam-cosmic', 'super-cosmic': 'holo-lam-cosmic', 'supercosmic': 'holo-lam-cosmic',
    'holo-hex': 'holo-lam-hex', 'holo-lam-hex': 'holo-lam-hex', 'hex': 'holo-lam-hex',
    'holo-hearts': 'holo-lam-hearts', 'holo-lam-hearts': 'holo-lam-hearts', 'hearts': 'holo-lam-hearts', 'heart-holo': 'holo-lam-hearts'
  };
  if (lam && LAM[lam]) {
    if (LAM[lam] === 'glossy' || LAM[lam] === 'matte') {
      if (typeof setFinish === 'function') setFinish(LAM[lam]);
    } else if (typeof setSpecialtyFinish === 'function') {
      setSpecialtyFinish(LAM[lam]);
    }
  }
  var EMB = { none: 'none', flat: 'none', light: 'std', std: 'std', standard: 'std', hd: 'std', heavy: 'heavy' };
  if (emb && EMB[emb]) {
    setEmboss(EMB[emb]);
  } else if (currentCat === 'embossed-stickers') {
    setEmboss('std');
  }
  updateEmbossSection();
  // Josh 10/31 deals page: ?size=3x3&qty=200 opens the builder on that size + quantity (a deal's button). A size or
  // quantity this product doesn't list is ignored, so the builder keeps its own default.
  var sizeWant = String(params.get('size') || '').toLowerCase().replace(/\s+/g, '');
  var sizeList = document.getElementById('sqSizeList');
  if (/^\d+(\.\d+)?x\d+(\.\d+)?$/.test(sizeWant) && sizeList && typeof activatePill === 'function') {
    var sizeHit = Array.from(sizeList.querySelectorAll('input[type=radio]')).find(function (r) {
      return r.value.toLowerCase().replace(/["\s]/g, '').replace(/\u00d7/g, 'x') === sizeWant;
    });
    if (sizeHit) { activatePill(sizeList, sizeHit.value); if (typeof onSizeChange === 'function') onSizeChange(true); }
  }
  var qtyWant = String(params.get('qty') || '');
  var qtyList = document.getElementById('sqQtyList');
  if (/^\d{1,6}$/.test(qtyWant) && qtyList && typeof setQty === 'function' &&
      qtyList.querySelector('input[type=radio][value="' + qtyWant + '"]')) {
    setQty(qtyWant);
  }
  if ((sizeWant || qtyWant) && typeof updatePrice === 'function') updatePrice();
}

// ─── JAR COLOR SWATCHES ─────────────────────────────────────────────────────

function buildJarColorSwatches() {
  var grid = document.getElementById('jarColorGrid');
  if (!grid) return;
  grid.innerHTML = '';
  JAR_COLORS.forEach(function(jc) {
    var sw = document.createElement('div');
    sw.className = 'mat-swatch' + (jc.id === currentJarColor ? ' active' : '');
    sw.onclick = (function(cid) { return function() { selectJarColor(cid); }; })(jc.id);
    sw.innerHTML = '<img src="' + jc.img + '" alt="' + jc.label + '">' +
                   '<span>' + jc.label + '</span>';
    grid.appendChild(sw);
  });
}

function selectJarColor(id) {
  currentJarColor = id;
  document.querySelectorAll('#jarColorGrid .mat-swatch').forEach(function(el, i) {
    el.classList.toggle('active', JAR_COLORS[i] && JAR_COLORS[i].id === id);
  });
  // Update static preview to the matching jar SVG
  var jc = null;
  for (var i = 0; i < JAR_COLORS.length; i++) {
    if (JAR_COLORS[i].id === id) { jc = JAR_COLORS[i]; break; }
  }
  if (jc && !artworkDataUrl) {
    var imgEl = document.getElementById('productImg');
    imgEl.src = jc.img;
    currentProductImageUrl = jc.img;
    if (CATEGORIES[currentCat]) CATEGORIES[currentCat]._customImage = jc.img;
  }
  // If artwork is already loaded re-spin the 3D with the new glass color
  if (artworkDataUrl) {
    preview3D.activate(artworkDataUrl);
  }
}

function updateJarColorSection() {
  var section = document.getElementById('jarColorSection');
  if (!section) return;
  var cat = CATEGORIES[currentCat];
  var customName = cat && cat._customName;
  var show = (currentCat === 'labels' && customName === 'Flower Jar Labels');
  section.style.display = show ? '' : 'none';
  if (show) buildJarColorSwatches();
}

// ─── UPLOAD TABS ────────────────────────────────────────────────────────────

function setUploadTab(tab) {
  currentUploadTab = tab;
  // Jar tabs
  var tabBody = document.getElementById('tabBody');
  var tabLid  = document.getElementById('tabLid');
  if (tabBody) tabBody.classList.toggle('active', tab === 'body');
  if (tabLid)  tabLid.classList.toggle('active',  tab === 'lid');
  // Mylar tabs
  var tabMF = document.getElementById('tabMylarFront');
  var tabMB = document.getElementById('tabMylarBack');
  var tabMBo = document.getElementById('tabMylarBottom');
  if (tabMF)  tabMF.classList.toggle('active',  tab === 'mylar-front');
  if (tabMB)  tabMB.classList.toggle('active',  tab === 'mylar-back');
  if (tabMBo) tabMBo.classList.toggle('active', tab === 'mylar-bottom');
  // Zone visibility — mylar-front reuses the body zone
  var zBody  = document.getElementById('uploadZoneBody');
  var zLid   = document.getElementById('uploadZoneLid');
  var zMBack = document.getElementById('uploadZoneMylarBack');
  var zMBot  = document.getElementById('uploadZoneMylarBottom');
  if (zBody)  zBody.style.display  = (tab === 'body' || tab === 'mylar-front') ? '' : 'none';
  if (zLid)   zLid.style.display   = tab === 'lid'          ? '' : 'none';
  if (zMBack) zMBack.style.display  = tab === 'mylar-back'  ? '' : 'none';
  if (zMBot)  zMBot.style.display   = tab === 'mylar-bottom' ? '' : 'none';
  // Print-file button only applies to body / mylar-front panel
  var dlBtn = document.getElementById('printDlBtn');
  if (dlBtn) dlBtn.style.display = (tab === 'lid' || tab === 'mylar-back' || tab === 'mylar-bottom') ? 'none' : '';

  // Sync 3D preview to the selected panel.
  // mylarCurrentView is private inside preview3D — do NOT check it here.
  // preview3D.setCameraView is a no-op when 3D isn't active (t3SetCameraView is null).
  var pvMap = { 'mylar-front': 'front', 'mylar-back': 'back', 'mylar-bottom': 'bottom' };
  var targetView = pvMap[tab];
  if (targetView && currentCat === 'packaging' && typeof preview3D !== 'undefined') {
    preview3D.setCameraView(targetView);
  }
}

function updateUploadTabs() {
  var cat = CATEGORIES[currentCat];
  var customName = cat && cat._customName;
  var isJar   = (currentCat === 'labels' &&
    (customName === 'Flower Jar Labels' || customName === 'Miron Jar Labels'));
  var isMylar = (currentCat === 'packaging' && customName === 'Mylar Bags');

  // Jar tab buttons
  var tabBody = document.getElementById('tabBody');
  var tabLid  = document.getElementById('tabLid');
  if (tabBody) tabBody.style.display = isJar ? '' : 'none';
  if (tabLid)  tabLid.style.display  = isJar ? '' : 'none';
  // Mylar tab buttons
  var tabMF  = document.getElementById('tabMylarFront');
  var tabMB  = document.getElementById('tabMylarBack');
  var tabMBo = document.getElementById('tabMylarBottom');
  if (tabMF)  tabMF.style.display  = isMylar ? '' : 'none';
  if (tabMB)  tabMB.style.display  = isMylar ? '' : 'none';
  if (tabMBo) tabMBo.style.display = isMylar ? '' : 'none';

  var tabs = document.getElementById('uploadTabs');
  if (tabs) tabs.classList.toggle('visible', isJar || isMylar);

  if (isMylar)     setUploadTab('mylar-front');
  else if (isJar)  setUploadTab('body');
  else             setUploadTab('body');
}

var productArtworkUploads = {};

function productArtworkUploadKey() {
  return currentCat + ':' + currentChipId;
}

function hasPendingProductArtwork() {
  var uploads = productArtworkUploads[productArtworkUploadKey()] || {};
  return Object.keys(uploads).some(function(slot) { return uploads[slot].pending; });
}

function beginProductArtworkUpload(slot, filename) {
  var key = productArtworkUploadKey();
  var uploads = productArtworkUploads[key] || (productArtworkUploads[key] = {});
  var ticket = uploads[slot] = { pending: true };
  var current = captureProductArtwork();
  current[slot] = null;
  current[slot + 'Original'] = null;
  current[slot + 'Filename'] = '';
  applyProductArtwork(current);
  updateCartButtonState();
  return function(preview, original) {
    if (uploads[slot] !== ticket) return false;
    ticket.pending = false;
    if (productArtworkUploadKey() === key) {
      if (preview) {
        var labels = { body: '[data-qa="upload-label"]', lid: '#lidUploadLabel', back: '#mylarBackLabel', bottom: '#mylarBottomLabel' };
        var label = document.querySelector(labels[slot]);
        if (label) label.innerHTML = '✓ <strong>' + escHtml(filename) + '</strong>';
      }
      return true;
    }
    // Josh 10/15: a file still loading when the shopper switched products is
    // dropped — artwork never follows them to, or back to, another product.
    return false;
  };
}

// Reject before any read/preview, clear this slot and cancel an earlier pending upload.
function rejectOversizeProductArtwork(input, slot) {
  if (input.files[0].size <= 20 * 1024 * 1024) return false;
  beginProductArtworkUpload(slot, '')(null, null);
  var labels = { body: '[data-qa="upload-label"]', lid: '#lidUploadLabel', back: '#mylarBackLabel', bottom: '#mylarBottomLabel' };
  var label = document.querySelector(labels[slot]);
  if (label) label.innerHTML = '<span class="upload-oversize-message">This file is over 20 MB. Add to cart now and email it to info@vinylpac.us after checkout with your order number.</span>';
  updateCartButtonState();
  return true;
}

function handleLidUpload(input) {
  if (!input.files || !input.files[0]) return;
  var file = input.files[0];
  if (rejectOversizeProductArtwork(input, 'lid')) return;
  var completeUpload = beginProductArtworkUpload('lid', file.name);
  var labelEl = document.getElementById('lidUploadLabel');
  if (labelEl) labelEl.innerHTML = '<strong>' + escHtml(file.name) + '</strong>';
  var isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  var reader = new FileReader();
  reader.onerror = function() {
    if (completeUpload(null, null)) {
      if (labelEl) labelEl.textContent = 'Could not read this file. Please choose it again.';
      updateCartButtonState();
    }
  };
  reader.onload = function(e) {
    var rawDataUrl = e.target.result;
    // Any file type (Josh 10/17): see handleUpload().
    var original = VPArtFiles.needsOriginal(file) ? VPArtFiles.dataUrl(rawDataUrl, file) : null;

    function applyLidArtwork(previewUrl) {
      if (!completeUpload(previewUrl, original)) return;
      lidArtworkDataUrl         = previewUrl;
      lidArtworkOriginalDataUrl = original;
      var prev = document.getElementById('uploadPreviewLid');
      if (prev) { prev.src = lidArtworkDataUrl; prev.style.display = 'block'; }
      if ((currentCat === 'labels' || currentCat === 'packaging') && typeof preview3D !== 'undefined') {
        preview3D.activate(artworkDataUrl);
        var previewProduct = productArtworkUploadKey();
        setTimeout(function() { if (productArtworkUploadKey() === previewProduct) preview3D.setCameraView('top'); }, 200);
      }
      updateCartButtonState();
    }

    if (isPdf) {
      renderPdfPage(rawDataUrl).then(function(pngDataUrl) {
        applyLidArtwork(pngDataUrl || rawDataUrl);
      });
    } else if (original) {
      VPArtFiles.preview(file).then(applyLidArtwork);
    } else {
      applyLidArtwork(rawDataUrl);
    }
  };
  reader.readAsDataURL(file);
}

// ─── MYLAR PANEL UPLOAD (Back / Bottom) ─────────────────────────────────────

function handleMylarPanelUpload(input, panel) {
  if (!input.files || !input.files[0]) return;
  var file = input.files[0];
  if (rejectOversizeProductArtwork(input, panel === 'back' ? 'back' : 'bottom')) return;
  var completeUpload = beginProductArtworkUpload(panel === 'back' ? 'back' : 'bottom', file.name);
  var labelId   = panel === 'back' ? 'mylarBackLabel'   : 'mylarBottomLabel';
  var previewId = panel === 'back' ? 'uploadPreviewMylarBack' : 'uploadPreviewMylarBottom';
  var labelEl = document.getElementById(labelId);
  if (labelEl) labelEl.innerHTML = '<strong>' + escHtml(file.name) + '</strong>';
  var isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  var reader = new FileReader();
  reader.onerror = function() {
    if (completeUpload(null, null)) {
      if (labelEl) labelEl.textContent = 'Could not read this file. Please choose it again.';
      updateCartButtonState();
    }
  };
  reader.onload = function(e) {
    var rawDataUrl = e.target.result;
    // Any file type (Josh 10/17): see handleUpload().
    var original = VPArtFiles.needsOriginal(file) ? VPArtFiles.dataUrl(rawDataUrl, file) : null;
    function applyPanelArtwork(previewUrl) {
      if (!completeUpload(previewUrl, original)) return;
      if (panel === 'back') {
        mylarBackArtworkDataUrl   = previewUrl;
        mylarBackOriginalDataUrl  = original;
      } else {
        mylarBottomArtworkDataUrl  = previewUrl;
        mylarBottomOriginalDataUrl = original;
      }
      var prev = document.getElementById(previewId);
      if (prev) { prev.src = previewUrl; prev.style.display = 'block'; }
      // Push artwork to the 3D bag panel directly — no front artwork required
      if (currentCat === 'packaging' && typeof preview3D !== 'undefined') {
        preview3D.activate(previewUrl);
      }
      updateCartButtonState();
    }
    if (isPdf) {
      renderPdfPage(rawDataUrl).then(function(pngDataUrl) {
        applyPanelArtwork(pngDataUrl || rawDataUrl);
      });
    } else if (original) {
      VPArtFiles.preview(file).then(applyPanelArtwork);
    } else {
      applyPanelArtwork(rawDataUrl);
    }
  };
  reader.readAsDataURL(file);
}

// ─── CATEGORY SWITCHING ──────────────────────────────────────────────────────

// The current product's default-material photo -- a fixed base image that material
// changes overlay a finish effect onto (see selectMaterial/showArtworkPreview),
// rather than swapping to a different pre-shot photo per material.
var currentDefaultBaseImage = '';

function switchCategory(id) {
  currentCat = id;
  var cat = CATEGORIES[id];

  // Tear down any active 3D preview and restore product image
  if (typeof preview3D !== 'undefined') preview3D.deactivate();

  // Clear any uploaded artwork
  setPreviewHint(true);
  artworkDataUrl = null;
  mylarBackArtworkDataUrl   = null;
  mylarBottomArtworkDataUrl = null;
  document.getElementById('artworkOverlay').innerHTML = '';
  var prev = document.getElementById('uploadPreview');
  prev.src = ''; prev.style.display = 'none';
  document.querySelector('.upload-label').innerHTML = UPLOAD_LABEL_DEFAULT;
  var productImg = document.getElementById('productImg');
  productImg.style.mixBlendMode = ''; // restore CSS multiply

  // Reset to this category's default material
  currentMat = cat.defaultMat || 0;
  document.querySelectorAll('#materialGrid .mat-swatch').forEach(function(el, i) {
    el.classList.toggle('active', i === currentMat);
    el.setAttribute('aria-pressed', String(i === currentMat));
  });

  // Update product image — prefer: URL ?image= param, then material-specific, then active chip, then category default
  var mat = MATERIALS[currentMat];
  var activeChipImg = (function() {
    var grp = SWITCHER_GROUPS[id];
    if (!grp || !currentChipId) return null;
    for (var i = 0; i < grp.length; i++) { if (grp[i].id === currentChipId) return grp[i].img; }
    return null;
  })();
  // For custom stickers the active chip image is canonical on load — material-specific
  // images apply only after the user explicitly picks a material.
  var newImg = cat._customImage
    || (currentCat === 'custom-stickers' ? activeChipImg : null)
    || (cat.materialImages && cat.materialImages[mat.id])
    || activeChipImg
    || cat.image;

  var imgEl = document.getElementById('productImg');
  imgEl.style.opacity = '0';
  currentProductImageUrl = newImg;
  // This runs right after currentMat was reset to cat.defaultMat above, so newImg
  // is always this product's default-material photo -- the fixed base that later
  // material changes overlay onto instead of swapping to a different photo.
  currentDefaultBaseImage = newImg;

  // Detect if this is the Mylar Bags product — hide productImg immediately
  // so the 3D canvas takes over cleanly with no broken-image flash.
  // Use cat._customName (set from ?product= URL param) rather than the
  // internal PRODUCTS table, which lives inside the preview3D closure.
  var _isMylar = id === 'packaging' &&
    (cat._customName || '').toLowerCase().indexOf('mylar') !== -1;

  if (_isMylar) {
    // Show the static mylar bag image immediately as a loading placeholder.
    // The 3D canvas will cover it once the GLB finishes loading.
    // Do NOT hide productImg here — the GLB onload callback does that.
    imgEl.src = newImg;
    imgEl.style.visibility = '';
    imgEl.style.opacity = '1';
  } else {
    imgEl.style.visibility = '';
    // Set src immediately so the correct image is always in place.
    imgEl.src = newImg;
    setTimeout(function() { imgEl.style.opacity = '1'; }, 50);
  }

  // Update product name (use custom name from ?product= param if present)
  var displayName = cat._customName || cat.name;
  document.getElementById('prodName').textContent = displayName;
  // Keep search/share metadata in step with direct entries, product switches and saved-item edits.
  var pageTitle = displayName + ' — VINYLPAC';
  var pageDescription = 'Order ' + displayName + ' from VINYLPAC. Choose your options and add artwork now or later.';
  var productUrl = new URL('https://vinylpac.us/');
  productUrl.searchParams.set('view', 'configurator');
  productUrl.searchParams.set('cat', currentCat);
  productUrl.searchParams.set('product', displayName);
  document.title = pageTitle;
  [
    ['meta[name="description"]', 'content', pageDescription],
    ['meta[property="og:title"]', 'content', pageTitle],
    ['meta[property="og:description"]', 'content', pageDescription],
    ['meta[property="og:url"]', 'content', productUrl.href],
    ['link[rel="canonical"]', 'href', productUrl.href]
  ].forEach(function(entry) {
    var tag = document.querySelector(entry[0]);
    if (tag) tag.setAttribute(entry[1], entry[2]);
  });
  var configCard = document.querySelector('.config-card');
  if (configCard) configCard.setAttribute('data-product', displayName);
  var builderTitle = document.getElementById('builderTitle');
  if (builderTitle) builderTitle.textContent = displayName;

  // Highlight the nav link whose href matches the active collection page.
  // window._navHighlight overrides the category id — used when a product lives
  // on a different collection page than its internal cat (e.g. jar labels on Packaging).
  var navTarget = (typeof window._navHighlight !== 'undefined' ? window._navHighlight : null) || id;
  document.querySelectorAll('#navLinks a').forEach(function(a) {
    var href = a.getAttribute('href') || '';
    a.classList.toggle('active', href !== '#' && href.indexOf(navTarget) > -1);
  });

  currentPackageSize = null;
  updatePrice();
  updateJarColorSection();
  updateDieCutUnderlaySection();
  updateUploadTabs();
  updatePackageSizeSection();
  // Rebuild switcher for the new context (group may have changed)
  buildProductSwitcher();
  // updatePackageSizeSection calls updateCartButtonState internally, but call again
  // here to handle non-packaging categories (which should always have button enabled)
  updateCartButtonState();

  // Show/hide finish section — hidden for categories with hideFinish:true (e.g. UV Labels)
  var finishSec = document.getElementById('finishSection');
  if (finishSec) finishSec.style.display = cat.hideFinish ? 'none' : '';
  updateEmbossSection();
  syncPrintProductUi();

  // Show 3D bag as soon as Mylar Bags is selected — no artwork needed.
  // Re-evaluate _isMylar AFTER buildProductSwitcher() has run: when arriving
  // directly from packaging.html (no ?product= URL param), cat._customName is
  // empty so the earlier check returns false. buildProductSwitcher() will have
  // defaulted currentChipId to the first chip (Mylar Bags), so we look that up
  // here and sync cat._customName so the rest of the page is consistent.
  if (!_isMylar && id === 'packaging') {
    var _activeChips = getChipsForContext();
    for (var _ci = 0; _ci < _activeChips.length; _ci++) {
      if (_activeChips[_ci].id === currentChipId &&
          (_activeChips[_ci].name || '').toLowerCase().indexOf('mylar') !== -1) {
        _isMylar = true;
        cat._customName = _activeChips[_ci].name; // sync so product name & display are correct
        break;
      }
    }
  }
  if (_isMylar && typeof preview3D !== 'undefined') {
    preview3D.activate(null);
  }
}

function updatePrintOptionButtons() {
  var kind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  var dims = kind === 'posters' ? vpGetCurrentDims() : null;
  var holoOk = !dims || vpPosterHoloFits(dims.w, dims.h);
  document.querySelectorAll('#printFinishToggle [data-print-finish]').forEach(function(btn) {
    var id = btn.getAttribute('data-print-finish');
    btn.classList.toggle('active', id === currentPrintFinish);
    btn.disabled = id !== 'satin' && !holoOk;
    btn.title = btn.disabled ? 'Holographic finishes go up to 18\u2033 \u00d7 24\u2033' : '';
  });
  document.querySelectorAll('#printSidesToggle [data-print-sides]').forEach(function(btn) {
    btn.classList.toggle('active', btn.getAttribute('data-print-sides') === currentPrintSides);
  });
}

function applyPrintPreviewFinish() {
  var kind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  if (!kind) return;
  var holoIdx = -1;
  if (typeof MATERIALS !== 'undefined') {
    for (var i = 0; i < MATERIALS.length; i++) {
      if (MATERIALS[i].id === 'holo') { holoIdx = i; break; }
    }
  }
  currentMat = (kind === 'posters' && vpPosterIsHolo() && holoIdx >= 0) ? holoIdx : 0;
  if (typeof showArtworkPreview === 'function') showArtworkPreview();
}

function setPrintFinish(id) {
  currentPrintFinish = id;
  updatePrintOptionButtons();
  applyPrintPreviewFinish();
  updatePrice();
}

function setPrintSides(id) {
  currentPrintSides = id;
  updatePrintOptionButtons();
  updatePrice();
}

function syncPrintProductUi() {
  var kind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  var matSec     = document.getElementById('stickerMaterialSection');
  var finishSec  = document.getElementById('finishSection');
  var surfaceSec = document.getElementById('surfaceFinishSection');
  var embossSec  = document.getElementById('embossSection');
  var printSec   = document.getElementById('printOptionsSection');
  var finishRow  = document.getElementById('printFinishRow');
  var sidesRow   = document.getElementById('printSidesRow');
  var lockedRow  = document.getElementById('printLockedRow');
  var lockedSz   = document.getElementById('printLockedSizeRow');
  var sizeWrap   = document.getElementById('sizeSelectWrap');
  var lockedTxt  = document.getElementById('printLockedText');
  var posterNote = document.getElementById('printPosterNote');
  var sampleNote = document.getElementById('stickerSampleNote');
  var summary    = document.getElementById('surfaceSelectionSummary');
  var customW    = document.getElementById('customW');
  var customH    = document.getElementById('customH');

  if (!kind) {
    if (matSec) matSec.style.display = '';
    if (printSec) printSec.style.display = 'none';
    if (posterNote) posterNote.style.display = 'none';
    var packaging = typeof getPackagingProduct === 'function' && getPackagingProduct();
    if (sizeWrap) sizeWrap.style.display = packaging ? 'none' : '';
    if (sampleNote) sampleNote.hidden = false;
    return;
  }

  if (matSec) matSec.style.display = 'none';
  if (finishSec) finishSec.style.display = 'none';
  if (surfaceSec) surfaceSec.hidden = true;
  if (embossSec) embossSec.style.display = 'none';
  if (printSec) printSec.style.display = '';
  if (sampleNote) sampleNote.hidden = true;
  if (summary) summary.hidden = true;

  if (kind === 'posters') {
    if (finishRow) finishRow.style.display = '';
    if (sidesRow) sidesRow.style.display = 'none';
    if (lockedRow) lockedRow.style.display = 'none';
    if (lockedSz) lockedSz.style.display = 'none';
    if (sizeWrap) sizeWrap.style.display = '';
    if (posterNote) posterNote.style.display = '';
    if (!VP_PRINT_FINISH[currentPrintFinish]) currentPrintFinish = 'satin';
    if (customW) { customW.min = '8'; customW.max = '36'; }
    if (customH) { customH.min = '8'; customH.max = '36'; }
  } else if (kind === 'yard-signs') {
    if (finishRow) finishRow.style.display = 'none';
    if (sidesRow) sidesRow.style.display = '';
    if (lockedRow) lockedRow.style.display = '';
    if (lockedSz) lockedSz.style.display = '';
    if (sizeWrap) sizeWrap.style.display = 'none';
    if (posterNote) posterNote.style.display = 'none';
    if (lockedTxt) lockedTxt.textContent = 'Satin · metal stand included';
    currentPrintFinish = 'satin';
    if (!currentPrintSides) currentPrintSides = 'single';
  } else {
    if (finishRow) finishRow.style.display = 'none';
    if (sidesRow) sidesRow.style.display = 'none';
    if (lockedRow) lockedRow.style.display = '';
    if (lockedSz) lockedSz.style.display = 'none';
    if (sizeWrap) sizeWrap.style.display = '';
    if (posterNote) posterNote.style.display = 'none';
    if (lockedTxt) lockedTxt.textContent = 'Unhemmed vinyl · grommets included';
    currentPrintFinish = 'unhemmed-grommets';
    if (customW) { customW.min = '12'; customW.max = '120'; }
    if (customH) { customH.min = '12'; customH.max = '120'; }
  }
  updatePrintOptionButtons();
  applyPrintPreviewFinish();
  if (typeof buildSizeSelect === 'function') buildSizeSelect();
  if (typeof buildQtySelect === 'function') buildQtySelect();
  if (typeof updateSizeTrigger === 'function') updateSizeTrigger();
  updatePrice();
}

// (pricing functions defined above init — see top of script)

// Big per-unit price beside the product name; the order total lives in the
// price bar above Add to Cart. Each new price rolls up (or down) from the
// last one, with a small pop, so a size or quantity change lands.
var vpHeadPriceShown = null;
var vpHeadPriceFrame = 0;
function vpPaintHeadPrice(unit, per) {
  var box    = document.getElementById('headPrice');
  var unitEl = document.getElementById('headPriceUnit');
  var perEl  = document.getElementById('headPricePer');
  if (!box || !unitEl) return;
  if (!(unit > 0)) return;
  var money = function(n) {
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  if (perEl) perEl.textContent = '/' + per;
  var from = vpHeadPriceShown == null ? 0 : vpHeadPriceShown;
  vpHeadPriceShown = unit;
  box.classList.add('is-ready');
  cancelAnimationFrame(vpHeadPriceFrame);
  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (still || from === unit) { unitEl.textContent = money(unit); return; }
  var start = performance.now();
  var step = function(now) {
    var t = Math.min(1, (now - start) / 560);
    var k = 1 - Math.pow(1 - t, 3);
    unitEl.textContent = money(from + (unit - from) * k);
    if (t < 1) vpHeadPriceFrame = requestAnimationFrame(step);
  };
  step(start);
  box.classList.remove('vp-price-pop');
  void box.offsetWidth;
  box.classList.add('vp-price-pop');
}

// Shared DOM write for the on-screen price. Command quotes and Sue totals both
// land here so the number shown is the same shape addToCart() charges.
function vpPaintPrice(cat, qty, finalUnit, total) {
  var totalEl  = document.getElementById('priceTotal');
  var unitEl   = document.getElementById('priceUnit');
  var noun = (typeof vpQtyNoun === 'function') ? vpQtyNoun(qty) : (cat.unit + 's');
  var per  = (typeof vpQtyNoun === 'function') ? vpQtyNoun(1) : cat.unit;
  if (totalEl) totalEl.textContent = '$' + total.toFixed(2);
  if (unitEl)  unitEl.textContent  = qty.toLocaleString() + ' ' + noun + '  ·  $' + finalUnit.toFixed(2) + '/' + per;
  vpPaintHeadPrice(finalUnit, per);

  window._vpCurrentUnit = finalUnit;
  window._vpCurrentQty  = qty;
  if (typeof refreshQtyOptionPrices === 'function') {
    try { refreshQtyOptionPrices(); } catch (e) {}
  }
}

// Embossing card: "+$X/sticker" = what flipping the switch adds to the price
// right now (embossed at Standard, or the picked depth, vs the shopper's
// laminate), from the same /api/quote the big price uses.
var _vpEmbUpCache = {};
function _vpSpecWith(emboss, finish, specialty) {
  var keep = [currentEmboss, currentFinish, currentSpecialty];
  currentEmboss = emboss; currentFinish = finish; currentSpecialty = specialty;
  try { return typeof vpQuoteSpec === 'function' ? vpQuoteSpec() : null; }
  finally { currentEmboss = keep[0]; currentFinish = keep[1]; currentSpecialty = keep[2]; }
}
function _vpQuoteEach(spec) {
  var key = vpQuoteKey(spec);
  if (!_vpEmbUpCache[key]) {
    _vpEmbUpCache[key] = fetch('/api/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(spec.laminate ? { w: spec.w, h: spec.h, qty: spec.qty, cut: spec.cut, finish: spec.finish, emboss: spec.emboss, laminate: spec.laminate }
        : { w: spec.w, h: spec.h, qty: spec.qty, cut: spec.cut, finish: spec.finish, emboss: spec.emboss })
    }).then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { var e = d && d.ok !== false ? Number(d.each) : NaN; return isFinite(e) ? e : null; })
      .catch(function() { delete _vpEmbUpCache[key]; return null; });
  }
  return _vpEmbUpCache[key];
}
function vpUpdateEmbossUpcharge() {
  var el = document.getElementById('embossUpcharge');
  if (!el) return;
  // 10/12: wherever the tag can show, it keeps its room ("+$0.00/sticker", unseen) while it has no price, so its
  // price landing or going never moves the card (configurator.html). The embossed line never shows it.
  if (currentCat === 'embossed-stickers') el.removeAttribute('data-reserve');
  else el.setAttribute('data-reserve', '+$0.00/' + (typeof vpQtyNoun === 'function' ? vpQtyNoun(1) : 'sticker'));
  var sec = document.getElementById('embossSection');
  if (!sec || sec.style.display === 'none' || currentCat === 'embossed-stickers') { el.textContent = ''; return; }
  var embossed = currentEmboss !== 'none';
  // Embossing off comes back on Gloss, so that is what the upcharge compares against.
  var lam = embossed ? { finish: 'glossy', specialty: 'none' } : { finish: currentFinish, specialty: currentSpecialty };
  var lamSpec = _vpSpecWith('none', lam.finish === 'none' ? 'glossy' : lam.finish, lam.specialty || 'none');
  var embSpec = _vpSpecWith(embossed ? currentEmboss : 'std', 'none', 'none');
  if (!lamSpec || !embSpec) { el.textContent = ''; return; }
  var want = vpQuoteKey(lamSpec) + '>' + vpQuoteKey(embSpec);
  el.dataset.want = want;
  Promise.all([_vpQuoteEach(lamSpec), _vpQuoteEach(embSpec)]).then(function(r) {
    if (el.dataset.want !== want) return;
    var diff = r[0] != null && r[1] != null ? r[1] - r[0] : null;
    var per = typeof vpQtyNoun === 'function' ? vpQtyNoun(1) : 'sticker';
    el.textContent = diff != null && diff >= 0.005 ? '+$' + diff.toFixed(2) + '/' + per : '';
  });
}

// The dropdown is always titled "Special instructions" (Josh 10/17: holo renaming it "White ink areas" needed fixing)
// and always shows the one plain line. The white-ink / raised-areas prompts that embossing, holo, chrome, gold and clear
// die cuts used to get are gone (Josh 10/18: "remove all that leave it blank or single line at most").
function vpSyncInstrHint() {
  var ta = document.getElementById('specialInstr');
  var hd = document.querySelector('#instrAccordion .accordion-header > span:first-child');
  if (!ta || !hd) return;
  var title = 'Special instructions', hint = 'Add any notes for your order\u2026';
  if (hd.textContent !== title) hd.textContent = title;
  if (ta.getAttribute('placeholder') !== hint) ta.setAttribute('placeholder', hint);
}

// "Cut directly to artwork edge" (Josh 10/17): a switch in Special instructions on Die Cut and Kiss Cut Transfer.
// On, the 3D sample drops its vinyl margin and the cart line carries "Cut: Directly to artwork edge".
var EDGE_CUT_CHIPS = { 'die-cut': 1, 'transfer': 1 };
var EDGE_CUT_LABEL = 'Directly to artwork edge';

function vpEdgeCutAllowed() {
  return !!EDGE_CUT_CHIPS[currentChipId];
}

function vpEdgeCutOn() {
  return !!currentEdgeCut && vpEdgeCutAllowed();
}

function vpSyncEdgeCut() {
  var row = document.getElementById('edgeCutRow');
  var sw = document.getElementById('edgeCutSwitch');
  if (!row || !sw) return;
  row.hidden = !vpEdgeCutAllowed();
  sw.setAttribute('aria-checked', vpEdgeCutOn() ? 'true' : 'false');
}

function setEdgeCut(on) {
  currentEdgeCut = !!on;
  vpSyncEdgeCut();
  if (window.vpStickerPreview && typeof window.vpStickerPreview.sync === 'function') window.vpStickerPreview.sync();
}

function updatePrice() {
  try { vpUpdateEmbossUpcharge(); } catch (e) {}
  try { vpSyncInstrHint(); } catch (e) {}
  try { vpSyncEdgeCut(); } catch (e) {}
  try {
    try {
      if (window.vpStickerPreview && typeof window.vpStickerPreview.sync === 'function') {
        window.vpStickerPreview.sync();
      }
    } catch (syncErr) {}

    var cat = CATEGORIES[currentCat];
    if (!cat) return;
    var qtyEl = document.getElementById('qtyInput');
    var qty = parseInt(qtyEl && qtyEl.value, 10);
    var printKind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
    if (printKind) {
      // Holo stops at 18×24: a bigger size falls back to Satin.
      if (printKind === 'posters') {
        var hDims = vpGetCurrentDims();
        if (vpPosterIsHolo() && !vpPosterHoloFits(hDims.w, hDims.h)) {
          currentPrintFinish = 'satin';
          applyPrintPreviewFinish();
        }
        updatePrintOptionButtons();
      }
      var printMin = vpPrintMinQty(printKind);
      var printQty = (!qty || qty < printMin) ? printMin : qty;
      if (qtyEl && printQty !== qty) qtyEl.value = printQty;
      qty = printQty;
      if (window._vpLastQuote) window._vpLastQuote = null;
      var pDims = vpGetCurrentDims();
      var printed = vpComputePrintTotal(printKind, pDims.w, pDims.h, qty);
      vpPaintPrice(cat, qty, printed.each, printed.total);
      var pDetails = document.getElementById('qtyCustomDetails');
      if (pDetails) {
        if (typeof getSelectedQtyValue === 'function' && getSelectedQtyValue() === 'custom') {
          if (!pDetails.classList.contains('qty-error')) {
            pDetails.textContent = '$' + printed.each.toFixed(2) + '/' + vpQtyNoun(1);
          }
        } else if (!pDetails.classList.contains('qty-error')) {
          pDetails.textContent = '';
        }
      }
      if (typeof updateCartButtonState === 'function') updateCartButtonState();
      return;
    }
    var spec = typeof vpQuoteSpec === 'function' ? vpQuoteSpec() : null;

    if (spec) {
      // Command path: the quote already includes finish/material. Do not apply
      // Sue matMult/finishMult/spotMult/markup on top.
      if (!qty || qty < 1) {
        qty = spec.qty || 50;
        if (qtyEl) qtyEl.value = qty;
        spec = vpQuoteSpec();
        if (!spec) return;
      }
      window._vpQuoteSeq = (window._vpQuoteSeq || 0) + 1;
      var seq = window._vpQuoteSeq;
      var key = vpQuoteKey(spec);
      var last = window._vpLastQuote;
      if (last && last.key === key && last.each != null) {
        vpPaintPrice(cat, qty, Number(last.each), Number(last.total != null ? last.total : last.each * qty));
        var liveDetails = document.getElementById('qtyCustomDetails');
        if (liveDetails) {
          if (typeof getSelectedQtyValue === 'function' && getSelectedQtyValue() === 'custom') {
            if (!liveDetails.classList.contains('qty-error')) {
              liveDetails.textContent = '$' + Number(last.each).toFixed(2) + '/' + cat.unit;
            }
          } else {
            liveDetails.textContent = '';
          }
        }
        refreshQtyOptionPrices();
      }
      updateCartButtonState();

      fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(Object.assign({
          w: spec.w,
          h: spec.h,
          qty: spec.qty,
          cut: spec.cut,
          finish: spec.finish,
          emboss: spec.emboss
        }, spec.laminate ? { laminate: spec.laminate } : {}))
      }).then(function(res) {
        if (!res.ok) throw new Error('quote http ' + res.status);
        return res.json();
      }).then(function(data) {
        if (seq !== window._vpQuoteSeq) return;
        if (!data || data.ok === false) return;
        var each = Number(data.each);
        if (!isFinite(each) || each < 0) return;
        var tot = data.total != null ? Number(data.total) : each * spec.qty;
        if (!isFinite(tot) || tot < 0) return;
        window._vpLastQuote = { key: key, each: each, total: tot, spec: spec };
        vpPaintPrice(cat, qty, each, tot);
        var detailsEl = document.getElementById('qtyCustomDetails');
        if (detailsEl) {
          if (typeof getSelectedQtyValue === 'function' && getSelectedQtyValue() === 'custom') {
            if (!detailsEl.classList.contains('qty-error')) {
              detailsEl.textContent = '$' + each.toFixed(2) + '/' + cat.unit;
            }
          } else {
            detailsEl.textContent = '';
          }
        }
        refreshQtyOptionPrices();
        updateCartButtonState();
      }).catch(function() {
        // Leave last good price. Do not zero the UI.
      });
      return;
    }

    // Sue path
    var pricing = loadPricing();
    var minQty = pricing.minQty || 20;
    if (!qty || qty < minQty) { qty = minQty; if (qtyEl) qtyEl.value = qty; }
    if (window._vpLastQuote) window._vpLastQuote = null;

    // ── Pricing engine v2: route to product-specific table; fall back to legacy ──
    var productType = vpGetProductType();
    var dims        = vpGetCurrentDims();
    var vpTotal     = vpComputeTotal(productType, dims.w, dims.h, qty);

    // Material/finish/laminate upcharges apply on top of whichever base price
    // is used below (v2 per-product table or legacy qty-tier formula) -- see
    // vpGetMultipliers() in pricing-engine.js, the single source of truth for
    // these admin-configurable multipliers.
    var mults      = vpGetMultipliers(pricing);
    var matMult    = mults.matMult;
    var finishMult = mults.finishMult;
    var spotMult   = mults.spotMult;
    var markup     = 1 + (pricing.markup || 0) / 100;

    var finalUnit, total, sizeMult;
    if (vpTotal !== null) {
      finalUnit = (vpTotal / qty) * matMult * finishMult * spotMult * markup;
      total     = Math.round(Number(finalUnit.toFixed(2)) * 100) * qty / 100;  // = what Shopify charges
    } else {
      // Legacy formula — Die Cut and unrecognised products
      var unitPrice = getUnitPriceForQty(qty, pricing);
      sizeMult  = getSizeMult(pricing);
      finalUnit = unitPrice * sizeMult * matMult * finishMult * spotMult * markup;
      total     = Math.round(Number(finalUnit.toFixed(2)) * 100) * qty / 100;  // = what Shopify charges
    }

    vpPaintPrice(cat, qty, finalUnit, total);

    // Quantity sub-row details — per-sticker + savings (only when Custom quantity active)
    var detailsEl = document.getElementById('qtyCustomDetails');
    if (detailsEl) {
      if (getSelectedQtyValue() === 'custom') {
        // Don't clobber an active validation error
        if (!detailsEl.classList.contains('qty-error')) {
          var baseUnit;
          if (vpTotal !== null) {
            var base50 = vpComputeTotal(productType, dims.w, dims.h, 50);
            baseUnit = base50 !== null
              ? (base50 / 50) * matMult * finishMult * spotMult * markup
              : finalUnit;
          } else {
            baseUnit = getUnitPriceForQty(50, pricing) * sizeMult * matMult * finishMult * spotMult * (1 + (pricing.markup || 0) / 100);
          }
          var savePct = baseUnit > 0 ? Math.round((baseUnit - finalUnit) / baseUnit * 100) : 0;
          detailsEl.textContent = '$' + finalUnit.toFixed(2) + '/' + cat.unit
            + (savePct > 0 ? '  (save ' + savePct + '%)' : '');
        }
      } else {
        detailsEl.textContent = '';
      }
    }

    // Refresh dropdown option labels with live costs for current configuration
    refreshQtyOptionPrices();
    // Re-evaluate add-to-cart gate (size, artwork) after any pricing update
    updateCartButtonState();
  } catch(e) {
    console.error('updatePrice error:', e);
  }
}



